import { buildTrafficPeriodQuery, type TrafficPeriodSelection, type TrafficPeriodView } from './workbench-analytics'

/**
 * 流量行为分析四维度契约（来源/访客/受访页/自定义事件），逐字段对齐后端
 * TrafficWorkbenchContracts 的 *AnalyticsView / *Row record（moduleCode 后端
 * null=全部模块，透传为 TS 的 `string | null`）。
 *
 * uv / visitors 口径：行级精确、跨行 sum 近似——每行的 uv / visitors 是该维度
 * 组合当日行内精确 distinct 访客数；totalUvApprox / totalVisitorsApprox 与
 * 跨日、跨行汇总按行相加，是近似口径（同一访客可能出现在多行/多日，会被重复计数）。
 */
export interface TrafficSourceRow {
  referrerKind: string
  referrerHost: string
  utmSource: string
  utmMedium: string
  utmCampaign: string
  pv: number
  sessions: number
  /** 行级精确 distinct 访客数；跨行相加为近似口径。 */
  uv: number
}

export interface TrafficSourceAnalytics {
  period: TrafficPeriodView
  moduleCode: string | null
  rows: TrafficSourceRow[]
  totalPv: number
  totalSessions: number
  /** 按行 uv 相加的近似合计（同一访客跨行/跨日会重复计数）。 */
  totalUvApprox: number
  metricDefinitionRef: 'tenant-traffic-v2'
}

export interface TrafficVisitorRow {
  clientType: string
  deviceOs: string
  deviceClass: string
  geoProvince: string
  visitorCohort: 'NEW' | 'RETURNING'
  /** 行级精确 distinct 访客数；跨行相加为近似口径。 */
  visitors: number
  sessions: number
  pv: number
}

export interface TrafficVisitorAnalytics {
  period: TrafficPeriodView
  moduleCode: string | null
  rows: TrafficVisitorRow[]
  /** 按行 visitors 相加的近似合计（同一访客跨行/跨日会重复计数）。 */
  totalVisitorsApprox: number
  metricDefinitionRef: string
}

export interface TrafficPageRow {
  pageCode: string
  pv: number
  /** 行级精确 distinct 访客数；跨行/跨日相加为近似口径。 */
  uv: number
  sessions: number
  averageActiveMillis: number
  entries: number
  exits: number
}

export interface TrafficPageAnalytics {
  period: TrafficPeriodView
  moduleCode: string | null
  rows: TrafficPageRow[]
  metricDefinitionRef: string
}

export interface TrafficCustomEventRow {
  eventName: string
  propKey: string
  propValue: string
  eventCount: number
  /** 行级精确 distinct 触发访客数；跨行相加为近似口径。 */
  uv: number
}

export interface TrafficCustomEventAnalytics {
  period: TrafficPeriodView
  moduleCode: string | null
  rows: TrafficCustomEventRow[]
  metricDefinitionRef: string
}

/** 维度查询所需的 api 形态（ApiClient 满足；测试可注入 fake）。 */
export interface TrafficDimensionApi {
  get<T>(path: string): Promise<T>
}

const DEFAULT_LIMIT = 10

/**
 * 服务端 Jackson 把 Long 统一序列化为字符串（平台级 WebStarterAutoConfiguration 的
 * jacksonLongToStringCustomizer），而本契约的计数字段声明为 number。取数边界统一
 * 收敛为 number，避免消费方 `Number.isFinite("12") === false` 把指标渲染成 '-'。
 */
function toCount(value: number | string): number {
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/**
 * 拼接维度查询串：周期段复用 buildTrafficPeriodQuery 的产物；moduleCode 为 null
 * （全部模块）时省略该参数；limit 缺省 10（合法域 1..50 由后端校验）。
 */
function buildTrafficDimensionQuery(
  moduleCode: string | null,
  period: TrafficPeriodSelection,
  limit?: number
): string {
  const query = new URLSearchParams(buildTrafficPeriodQuery(period))
  if (moduleCode !== null) {
    query.set('moduleCode', moduleCode)
  }
  query.set('limit', String(limit ?? DEFAULT_LIMIT))
  return query.toString()
}

export async function fetchTrafficSources(
  api: TrafficDimensionApi,
  moduleCode: string | null,
  period: TrafficPeriodSelection,
  limit?: number
): Promise<TrafficSourceAnalytics> {
  const query = buildTrafficDimensionQuery(moduleCode, period, limit)
  const payload = await api.get<TrafficSourceAnalytics>(
    `/api/saas/system/workbench/traffic/sources?${query}`
  )
  return {
    ...payload,
    rows: payload.rows.map((row) => ({
      ...row,
      pv: toCount(row.pv),
      sessions: toCount(row.sessions),
      uv: toCount(row.uv)
    })),
    totalPv: toCount(payload.totalPv),
    totalSessions: toCount(payload.totalSessions),
    totalUvApprox: toCount(payload.totalUvApprox)
  }
}

export async function fetchTrafficVisitors(
  api: TrafficDimensionApi,
  moduleCode: string | null,
  period: TrafficPeriodSelection,
  limit?: number
): Promise<TrafficVisitorAnalytics> {
  const query = buildTrafficDimensionQuery(moduleCode, period, limit)
  const payload = await api.get<TrafficVisitorAnalytics>(
    `/api/saas/system/workbench/traffic/visitors?${query}`
  )
  return {
    ...payload,
    rows: payload.rows.map((row) => ({
      ...row,
      visitors: toCount(row.visitors),
      sessions: toCount(row.sessions),
      pv: toCount(row.pv)
    })),
    totalVisitorsApprox: toCount(payload.totalVisitorsApprox)
  }
}

export async function fetchTrafficPages(
  api: TrafficDimensionApi,
  moduleCode: string | null,
  period: TrafficPeriodSelection,
  limit?: number
): Promise<TrafficPageAnalytics> {
  const query = buildTrafficDimensionQuery(moduleCode, period, limit)
  const payload = await api.get<TrafficPageAnalytics>(
    `/api/saas/system/workbench/traffic/pages?${query}`
  )
  return {
    ...payload,
    rows: payload.rows.map((row) => ({
      ...row,
      pv: toCount(row.pv),
      uv: toCount(row.uv),
      sessions: toCount(row.sessions),
      averageActiveMillis: toCount(row.averageActiveMillis),
      entries: toCount(row.entries),
      exits: toCount(row.exits)
    }))
  }
}

export async function fetchTrafficCustomEvents(
  api: TrafficDimensionApi,
  moduleCode: string | null,
  period: TrafficPeriodSelection,
  limit?: number
): Promise<TrafficCustomEventAnalytics> {
  const query = buildTrafficDimensionQuery(moduleCode, period, limit)
  const payload = await api.get<TrafficCustomEventAnalytics>(
    `/api/saas/system/workbench/traffic/events?${query}`
  )
  return {
    ...payload,
    rows: payload.rows.map((row) => ({
      ...row,
      eventCount: toCount(row.eventCount),
      uv: toCount(row.uv)
    }))
  }
}
